import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const CART_JSON_URL = `${window.Shopify?.routes?.root || '/'}cart.js`;
const CART_ADD_URL = Theme.routes.cart_add_url;
const CART_UPDATE_URL = Theme.routes.cart_update_url;

class CCFreeGiftManager extends HTMLElement {
  #busy = false;
  #controller = new AbortController();
  #previousGiftQuantities = new Map();
  #syncTimers = new Set();

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#controller.signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityIntent, {
      signal: this.#controller.signal,
    });
    this.#sync('initial');
  }

  disconnectedCallback() {
    this.#controller.abort();
    for (const timer of this.#syncTimers) clearTimeout(timer);
    this.#syncTimers.clear();
  }

  get giftIds() {
    return [Number(this.dataset.giftOne), Number(this.dataset.giftTwo)].filter(Boolean);
  }

  get threshold() {
    return Number(this.dataset.threshold) || 10000;
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  #declinedKey(cartToken, giftId) {
    return `cc-free-gift-declined:${cartToken}:${giftId}`;
  }

  #isDeclined(cartToken, giftId) {
    try {
      return localStorage.getItem(this.#declinedKey(cartToken, giftId)) === '1';
    } catch {
      return false;
    }
  }

  #setDeclined(cartToken, giftId, declined) {
    try {
      const key = this.#declinedKey(cartToken, giftId);
      if (declined) localStorage.setItem(key, '1');
      else localStorage.removeItem(key);
    } catch {
      // The cart still works when browser storage is unavailable.
    }
  }

  #onCartUpdate = (event) => {
    if (event.detail?.data?.source === 'cc-free-gifts') return;
    this.#sync('cart-update');
  };

  #onQuantityIntent = () => {
    // Quantity intent fires before Shopify finishes its request. Two bounded
    // follow-ups make the threshold reliable on both fast and slow mobile links.
    this.#scheduleSync(900);
    this.#scheduleSync(2600);
  };

  #scheduleSync(delay) {
    const timer = setTimeout(() => {
      this.#syncTimers.delete(timer);
      this.#sync('cart-update');
    }, delay);
    this.#syncTimers.add(timer);
  }

  async #fetchCart() {
    const response = await fetch(CART_JSON_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded');
    return response.json();
  }

  #paidTotal(cart) {
    // Shopify's cart total is the amount payable after product/cart discounts.
    // The gift variants cost 0 €, so they do not affect this threshold.
    return Number(cart.total_price || 0);
  }

  #giftQuantities(cart) {
    return new Map(
      this.giftIds.map((giftId) => {
        const quantity = cart.items
          .filter((item) => Number(item.variant_id) === giftId)
          .reduce((total, item) => total + Number(item.quantity || 0), 0);
        return [giftId, quantity];
      })
    );
  }

  async #mutateCart(payload, url) {
    const body = {
      ...payload,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart update failed');
    return data;
  }

  async #sync(reason) {
    if (this.#busy || this.giftIds.length !== 2 || !this.sectionId) return;
    this.#busy = true;

    try {
      let cart = await this.#fetchCart();
      const cartToken = cart.token || 'current';
      const paidTotal = this.#paidTotal(cart);
      const eligible = paidTotal >= this.threshold;
      let quantities = this.#giftQuantities(cart);

      if (reason === 'cart-update' && eligible) {
        for (const giftId of this.giftIds) {
          if ((this.#previousGiftQuantities.get(giftId) || 0) > 0 && (quantities.get(giftId) || 0) === 0) {
            this.#setDeclined(cartToken, giftId, true);
          }
        }
      }

      if (!eligible) {
        const updates = {};
        for (const giftId of this.giftIds) {
          this.#setDeclined(cartToken, giftId, false);
          if ((quantities.get(giftId) || 0) > 0) updates[giftId] = 0;
        }
        if (Object.keys(updates).length) {
          const result = await this.#mutateCart({ updates }, CART_UPDATE_URL);
          cart = await this.#fetchCart();
          quantities = this.#giftQuantities(cart);
          this.#announce(cart, result.sections);
        }
        this.#previousGiftQuantities = quantities;
        return;
      }

      const updates = {};
      const items = [];
      for (const giftId of this.giftIds) {
        const quantity = quantities.get(giftId) || 0;
        if (quantity > 1) updates[giftId] = 1;
        if (quantity === 0 && !this.#isDeclined(cartToken, giftId)) items.push({ id: giftId, quantity: 1 });
      }

      let result;
      if (Object.keys(updates).length) result = await this.#mutateCart({ updates }, CART_UPDATE_URL);
      if (items.length) result = await this.#mutateCart({ items }, CART_ADD_URL);

      if (result) {
        cart = await this.#fetchCart();
        quantities = this.#giftQuantities(cart);
        this.#announce(cart, result.sections);
      }

      this.#previousGiftQuantities = quantities;
    } catch (error) {
      console.error('Free gift sync failed', error);
    } finally {
      this.#busy = false;
    }
  }

  #announce(cart, sections) {
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.sectionId, {
        source: 'cc-free-gifts',
        itemCount: cart.item_count,
        sections,
      })
    );
  }
}

if (!customElements.get('cc-free-gift-manager')) {
  customElements.define('cc-free-gift-manager', CCFreeGiftManager);
}
